import { useMemo, useState, type ReactNode } from 'react';
import { SpendCurve, SpendLegend, formatMoney } from '@/components/ui/charts';
import { ErrorMessage } from '@/components/ui/error-message';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import { WorkspaceField, WorkspaceInput } from '@/components/ui/workspace/field';
import { AddBudgetEntryCard, BudgetSetupCard } from '@/components/health/budget-forms';
import { useCanAdjustAssessment } from '@/lib/api/health-readiness';
import {
  amountCents,
  budgetPlanDraft,
  formatAmount,
  formatVariancePct,
  pctVariance,
  planBody,
  planDraftTotals,
  planMonthLabel,
  useEngagementBudgetQuery,
  usePutBudgetPlanMutation,
  type EngagementBudget,
} from '@/lib/api/health-budget';
import { cn } from '@/lib/utils/cn';
import { spendMonths } from './detail-model';
import { DetailCard, Meta, OFORM, P_LABEL } from './detail-parts';

/**
 * The Budget domain's spend (T-0243 / E22-F02; prototype `budgetBlock()`,
 * `sCurve()`): three tiles (authorised, spent to date, projected at
 * completion), the cumulative spend against plan with the authorised
 * ceiling (`SpendCurve`), and for a lead the monthly plan editor. Then the
 * spend and change-order entry, or the baseline setup when there is no
 * budget yet.
 *
 * Every figure is the budget API's: the authorised ceiling, spend and plan
 * to date, the projection, the series and the saved plan's difference.
 * Each tile's second line compares two of them (spend against the plan to
 * date, the projection against the ceiling); nothing is added up.
 */
export function BudgetBlock({ engagementId }: { engagementId: string }) {
  const canLead = useCanAdjustAssessment();
  const budget = useEngagementBudgetQuery(engagementId, { includeSeries: true });
  const today = useMemo(() => new Date(), []);
  const [editing, setEditing] = useState(false);

  if (budget.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading budget…
      </div>
    );
  }
  if (budget.error) return <ErrorMessage error={budget.error} />;
  if (!budget.data) return <BudgetSetupCard engagementId={engagementId} />;
  const b = budget.data;
  const variance = pctVariance(b.actualToDate, b.planToDate);
  const headroom = b.authorised - b.projectedTotal;
  const planOff = b.planDifference !== null && Math.abs(b.planDifference) >= 0.005;

  return (
    <>
      <DetailCard
        title="Budget"
        aside={<Meta>contract facts, approved change orders and the actual-spend entries</Meta>}
      >
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
          <Kpi
            label="Authorised"
            value={formatMoney(b.authorised)}
            trend={
              b.changeOrderDelta === 0
                ? `Contract ${formatMoney(b.originalContract)}, no change orders`
                : `Contract ${formatMoney(b.originalContract)} ${b.changeOrderDelta > 0 ? '+' : '−'} change orders ${formatMoney(Math.abs(b.changeOrderDelta))}`
            }
            source="Contract + approved change orders"
          />
          <Kpi
            label="Spent to date"
            value={formatMoney(b.actualToDate)}
            trend={
              variance === null ? (
                'no plan to date yet'
              ) : (
                <>
                  <b className="font-semibold text-ink-900">{formatVariancePct(variance)}</b> vs
                  plan {formatMoney(b.planToDate)}
                </>
              )
            }
            source="Actual-spend entries"
          />
          <Kpi
            label="Projected at completion"
            value={formatMoney(b.projectedTotal)}
            trend={
              Math.abs(headroom) < 0.5
                ? 'on the authorised total'
                : `${formatMoney(Math.abs(headroom))} ${headroom > 0 ? 'under' : 'over'} authorised`
            }
            source="Spend to date + plan remaining"
          />
        </div>
        {planOff ? (
          <p role="status" className="mt-3 text-meta text-posture-at-risk">
            The monthly plan is {formatAmount(b.planDifference as number)}{' '}
            {(b.planDifference as number) > 0 ? 'over' : 'under'} the authorised total;{' '}
            {canLead ? 'edit the plan to match.' : 'a lead edits the plan to match.'}
          </p>
        ) : null}
        <div className="mt-3.5">
          <SpendCurve months={spendMonths(b.series, today)} authorised={b.authorised} />
          {b.series.length > 0 ? <SpendLegend /> : null}
        </div>
        {canLead ? (
          editing ? (
            <PlanEditor
              engagementId={engagementId}
              budget={b}
              today={today}
              onDone={() => setEditing(false)}
            />
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <WorkspaceButton onClick={() => setEditing(true)}>Edit monthly plan</WorkspaceButton>
              <span className="text-meta text-ink-500">
                {b.planCurveType === 'custom'
                  ? 'The plan line follows the monthly plan.'
                  : 'The plan line is straight across the contract dates until a monthly plan is saved.'}
              </span>
            </div>
          )
        ) : null}
      </DetailCard>
      <AddBudgetEntryCard engagementId={engagementId} />
    </>
  );
}

function Kpi({
  label,
  value,
  trend,
  source,
}: {
  label: string;
  value: string;
  trend: ReactNode;
  source: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-card border border-ink-100 bg-paper-0 px-5 py-4">
      <span className="text-meta font-medium text-ink-500">{label}</span>
      <span className="text-[24px] font-semibold leading-[30px] tracking-[-0.01em] text-ink-900 tabular-nums">
        {value}
      </span>
      <span className="text-meta text-ink-500">{trend}</span>
      <KpiSourcePill source={source} className="mt-0.5 w-fit" />
    </div>
  );
}

/**
 * The monthly plan (T-0240's PUT …/budget/plan): one amount per month,
 * opened with the saved plan or the even split of the authorised total.
 * The typed total and its difference to the authorised total show before
 * saving; Save waits until they match, and the API's own refusal (a change
 * order approved meanwhile, say) shows as it words it.
 */
function PlanEditor({
  engagementId,
  budget,
  today,
  onDone,
}: {
  engagementId: string;
  budget: EngagementBudget;
  today: Date;
  onDone: () => void;
}) {
  const put = usePutBudgetPlanMutation(engagementId);
  const [rows, setRows] = useState(() => budgetPlanDraft(budget, today));
  const totals = planDraftTotals(rows, budget.authorised);
  const balanced = totals.valid && Math.abs(totals.difference) < 0.005;
  const state = !totals.valid
    ? 'every month needs an amount'
    : balanced
      ? 'adds up to the authorised total'
      : `${formatAmount(totals.difference)} ${totals.difference > 0 ? 'over' : 'under'} the authorised total`;

  return (
    <form
      aria-label="Monthly spend plan"
      className={cn(OFORM, 'mt-3.5')}
      onSubmit={(e) => {
        e.preventDefault();
        if (!balanced || put.isPending) return;
        put.mutate(planBody(rows), { onSuccess: onDone });
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className={P_LABEL}>Monthly spend plan</span>
        <span className="text-meta text-ink-500">
          The months add up to the authorised total, {formatAmount(budget.authorised)}.
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {rows.map((r, i) => (
          <WorkspaceField key={r.month} label={planMonthLabel(r.month)}>
            {(id) => (
              <WorkspaceInput
                id={id}
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={r.amount}
                aria-invalid={amountCents(r.amount) === null ? true : undefined}
                onChange={(e) =>
                  setRows((all) =>
                    all.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)),
                  )
                }
                className="px-2 font-mono text-body-sm"
              />
            )}
          </WorkspaceField>
        ))}
      </div>
      <p
        data-testid="plan-difference"
        className={cn('text-meta', balanced ? 'text-posture-healthy' : 'text-posture-at-risk')}
      >
        Plan total {formatAmount(totals.total)} · {state}
      </p>
      {put.error ? <ErrorMessage error={put.error} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <WorkspaceButton type="submit" variant="primary" disabled={!balanced || put.isPending}>
          {put.isPending ? 'Saving…' : 'Save plan'}
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onDone} disabled={put.isPending}>
          Cancel
        </WorkspaceButton>
        <span className="text-meta text-ink-500">
          Saving replaces the plan; the plan line and the projection follow it.
        </span>
      </div>
    </form>
  );
}
